import { defineStore } from "pinia"
import { ref } from "vue"

interface Toast {
  /**
   * The message content of the toast
   */
  message: string
  /**
   * The type, can be info, success, or danger
   */
  type: "info"|"success"|"danger"
  /**
   * How long the user will have a chance to see the toast
   */
  showForSeconds: number
  /**
   * The timestamp at which this toast has been created
   */
  timestamp: number
  /**
   * This timeout can be used by the displaying component to start a countdown
   * until the toast is no longer shown.
   */
  timeout?: ReturnType<typeof setTimeout>
  /**
   * Optional. If present, this ensures that only a single toast with this key
   * is present.
   */
  key?: string
}

export const useToastStore = defineStore("toast", () => {
  const toasts = ref<Toast[]>([])

  /**
   * Adds a new toast to be shown to the user (by default "info" for 5sec).
   * 
   * @param  {string}  message         The text content for this toast
   * @param  {string}  type            The type, defaults to `info`, otherwise
   *                                   `success` or `danger`.
   * @param  {number}  showForSeconds  How long to show it (default 5 seconds).
   * @param  {string}  key             If given, ensures that following toasts
   *                                   with the same key will replace this one,
   *                                   ensuring there is only one.
   */
  function toast (
    message: string,
    type: "info"|"success"|"danger" = "info",
    showForSeconds: number = 3,
    key?: string
  ) {
    const newToast: Toast = { message, type, showForSeconds, timestamp: Date.now(), key }
    if (key === undefined) {
      toasts.value.push(newToast)
    } else if (toasts.value.find(t => t.key === key) === undefined) {
      toasts.value.push(newToast)
    }
  }

  /**
   * Dismisses the provided toast right away, without waiting for the timer.
   *
   * @param   {Toast}  toast  The toast to dismiss
   */
  function dismissToast (toast: Toast) {
    clearTimeout(toast.timeout)
    toasts.value.splice(toasts.value.indexOf(toast), 1)
  }

  return { toasts, toast, dismissToast }
})
